import Error from './error/error-page';
import { formatPageTitle } from 'app/utilities/format';
import Helmet from 'react-helmet';
import { isAppProd } from 'config/env';
import JsonLd from 'app/utilities/JsonLd';
import NotFound from './error/not-found-page';
import PropTypes from 'prop-types';
import React from 'react';
import { RESPONSE } from 'config/api';
import { siteTitle } from 'config/seo';
import Template from 'app/components/templates/index';
import templateType from 'app/utilities/template-type';
import {
    getBreadcrumbStructuredData,
    getContentBlocksStructuredData,
    getPageCanonicalUrl,
    getStructuredDataForPage
} from 'app/utilities/seo';

const ContentPage = ({ page }) => {
    // Render not error/not found page
    if (!page || page.error && page.error.status === RESPONSE.INTERNAL_SERVER_ERROR) return <Error />;
    if (page.error && page.error.status === RESPONSE.NOT_FOUND || !page.attributes) return <NotFound />;

    const { type, attributes } = page;
    const {
        is_published: isPublished,
        name: title,
        seo_description: seoDescription,
        seo_keywords: seoKeywords,
        seo_title: seoTitle,
        slug,
        blocks = [],
        main_images: mainImages = [],
        thumbnail,
        start_date: startDate,
        end_date: endDate
    } = attributes;

    const image = mainImages && mainImages[0] || thumbnail;
    const isNoIndex = !isAppProd || !isPublished;

    const titleToUse = seoTitle || title;
    const pageUrl = getPageCanonicalUrl(page);

    // Format title for event and exhibition types
    const titleToShow = slug === 'home' ?
        siteTitle :
        formatPageTitle(type, titleToUse, startDate, endDate);
    const pageStructuredData = getStructuredDataForPage(page, {
        description: seoDescription,
        image,
        title: titleToUse,
        url: pageUrl
    });
    const breadcrumbStructuredData = getBreadcrumbStructuredData(page);
    const contentBlocksStructuredData = getContentBlocksStructuredData(blocks, {
        title: titleToUse,
        url: pageUrl
    });

    return (
        <main className="page" role="main">
            <Helmet>
                {isNoIndex && <meta name="robots" content="noindex, nofollow" />}

                <title>{titleToShow}</title>
                <meta name="description" content={seoDescription} />
                <meta name="keywords" content={seoKeywords} />
                <link rel="canonical" href={pageUrl} />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={titleToShow} />
                <meta name="twitter:description" content={seoDescription} />
                {image && <meta name="twitter:image" content={image} />}

                {/* Open Graph */}
                <meta property="og:locale" content="en_NZ" />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={titleToShow} />
                <meta property="og:description" content={seoDescription} />
                <meta property="og:url" content={pageUrl} />
                {image && <meta property="og:image" content={image} />}
            </Helmet>
            <JsonLd item={pageStructuredData} />
            <JsonLd item={breadcrumbStructuredData} />
            <JsonLd item={contentBlocksStructuredData} />
            <Template
                type={templateType(slug, type)}
                data={page.attributes}
            />
        </main>
    );
};

ContentPage.propTypes = {
    page: PropTypes.object.isRequired
};

export default ContentPage;
